import React, { useContext } from 'react'
import { Contract } from 'components/review_tiles/Contract'
import { ReviewSource } from 'components/review_tiles/ReviewSource'
import { ReviewIssueTrackingCTA } from 'components/review_tiles/ReviewIssueTrackingCTA'
import { FeatureContext } from 'components/FeatureContext'
import { editReviewPath } from 'components/routes/path'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'

export type ReviewTileRenderMode = 'default' | 'messageReviewerModal'
export type IssueTrackingCTAMode = 'default' | 'hidden' | 'activeOnly'
export type ReviewTileEditActionMode = 'default' | 'hidden'
type ReviewTileVisibility = 'default' | 'hidden'

const defaultVisibilityForRenderMode = (renderMode: ReviewTileRenderMode): ReviewTileVisibility => (
  renderMode === 'messageReviewerModal' ? 'hidden' : 'default'
)

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const year = new Intl.DateTimeFormat('en-US', { year: '2-digit' }).format(date)
  const month = new Intl.DateTimeFormat('en-US', { month: 'numeric' }).format(date)
  const day = new Intl.DateTimeFormat('en-US', { day: '2-digit' }).format(date)

  return `${month}/${day}/${year}`
}

interface ReviewTileFooterProps {
  review: ReviewType
  renderMode?: ReviewTileRenderMode
  onRaiseIssueClick?: () => void
  onActiveIssueClick?: () => void
  onIssueStatusChange?: () => void | Promise<void>
  issueTrackingCTAMode?: IssueTrackingCTAMode
  editActionMode?: ReviewTileEditActionMode
}

export function ReviewTileFooter ({
  review,
  renderMode = 'default',
  onRaiseIssueClick,
  onActiveIssueClick,
  onIssueStatusChange,
  issueTrackingCTAMode = defaultVisibilityForRenderMode(renderMode),
  editActionMode = defaultVisibilityForRenderMode(renderMode)
}: ReviewTileFooterProps): React.ReactElement {
  const featureFlags = useContext(FeatureContext)
  const issueTrackingEnabled = featureFlags?.issueTracking ?? false
  const { contract, contract_vehicle: contractVehicle } = review

  return (
    <div className='grid-x review-footer'>
      <div className='contract-wrapper'>
        {(contract != null || contractVehicle != null) &&
          <Contract contract={contract} contractVehicle={contractVehicle} reviewCanEdit={review.can_edit} />}
      </div>
      <span className='flex-child-grow' />

      {review.can_edit && editActionMode !== 'hidden' && (
        <span className='review-control'>
          <a href={editReviewPath(review.id)} className='edit'>
            <i className='fa-solid fa-square-pen color-pro-steel-grey-700' aria-hidden='true' /> Edit
          </a>
        </span>
      )}

      {review.share_with_supplier && (
        <span className='review-control info-text'>Shared with Supplier</span>
      )}

      <span className='review-control info-text'>
        {formatDate(review.review_date)}
      </span>

      <span className='review-control info-text'>
        <ReviewSource review={review} />
      </span>

      {issueTrackingCTAMode !== 'hidden' && (
        <ReviewIssueTrackingCTA
          review={review}
          issueTrackingEnabled={issueTrackingEnabled}
          onRaiseIssueClick={onRaiseIssueClick}
          onActiveIssueClick={onActiveIssueClick}
          onIssueStatusChange={onIssueStatusChange}
          variant={issueTrackingCTAMode === 'activeOnly' ? 'activeOnly' : 'default'}
        />
      )}
    </div>
  )
}
